import { z } from "zod";

// TES-1194 (Ö-388 c, Ö-389 b; Emelie 2026-10-08) — the doctor's control of the
// regular weekly dose day, on the patient's treatment view in admin:
//   dose_day_moved       — "Ändra ordinarie dosdag": a new dose day from a
//                          date on; doctor only, never the coach;
//   restart_after_pause  — "Ny startdag efter uppehåll": the medically
//                          confirmed first dose after a pause, from which the
//                          schedule counts again.
// `anchorOn` is the first planned dose day of the new series, a Stockholm
// calendar date. The patient's own registrations never move the schedule.
export const DOSE_SCHEDULE_ANCHOR_REASONS = [
  "dose_day_moved",
  "restart_after_pause",
] as const;
export type DoseScheduleAnchorReason =
  (typeof DOSE_SCHEDULE_ANCHOR_REASONS)[number];

export const setDoseScheduleAnchorSchema = z.object({
  reason: z.enum(DOSE_SCHEDULE_ANCHOR_REASONS),
  anchorOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD"),
  // The doctor's own note on why; optional, kept encrypted.
  note: z.string().trim().max(500).optional(),
});
export type SetDoseScheduleAnchorRequest = z.infer<
  typeof setDoseScheduleAnchorSchema
>;

// TES-1522 (Ö-789 b) — a corrected first dose re-anchors her future schedule
// as an anchor of its own. Never set through the doctor's two controls
// above: it comes from the first-dose correction, hers or the doctor's.
export type DoseScheduleAnchorRowReason =
  | DoseScheduleAnchorReason
  | "first_dose_corrected";

export interface DoseScheduleAnchorRow {
  id: string;
  anchorOn: string;
  reason: DoseScheduleAnchorRowReason;
  note: string | null;
  // The doctor who set it; null for her own first-dose correction.
  setByName: string | null;
  createdAt: string;
}

// GET/POST employee-api/v1/patients/:patientId/dose-schedule.
export interface DoseScheduleView {
  // A once-a-week ordination with a registered first dose: there is a
  // schedule to show or change at all.
  scheduled: boolean;
  firstDoseOn: string | null;
  // The first day of the series that is (or will be, after a future change)
  // running: its weekday is the regular dose day.
  regularFrom: string | null;
  // The next planned dose day today or later, or null (no schedule, paused,
  // or waiting for a confirmed restart).
  nextDoseOn: string | null;
  // The treatment or the cycle is paused right now.
  paused: boolean;
  // A pause ended and no new start date has been confirmed since (ISO
  // instant of the resume): "Ny startdag efter uppehåll" is offered.
  awaitingRestartSince: string | null;
  // The earliest date each control accepts, Stockholm calendar dates; null
  // when the control is not offered.
  moveFrom: string | null;
  restartFrom: string | null;
  // Newest first.
  anchors: DoseScheduleAnchorRow[];
}

// TES-1151 S7 (Ö-423, Emelie 2026-10-15) — her dose log as the doctor reads
// it: "Dosloggen är medicinsk behandlingsinformation och ska vara synlig för
// läkaren och andra uttryckligen behöriga medicinska roller. Coachen ska
// inte få direkt åtkomst … Läsning och ändringar av dosuppgifter ska
// auditloggas." GET employee-api/v1/patients/:patientId/dose-log, doctor
// only; every read is a PDL access-log row.
//
// Ö-424 keeps two statuses apart: "Ingen dos tagen" is her registration
// (`outcome: not_taken`), "Uppehåll enligt läkare" is a doctor's pause
// (`pauses`). Neither changes the prescription.
export interface DoseLogEntryView {
  id: string;
  // first_dose = CP-12; scheduled = an answer to a planned dose day.
  kind: "first_dose" | "scheduled";
  // The planned dose day it answers; null for the first dose.
  plannedOn: string | null;
  outcome: "taken" | "not_taken";
  // The day she took it (Stockholm); null for not_taken and for answers
  // from before the date was asked.
  takenOn: string | null;
  // "HH:MM", optional.
  takenAtTime: string | null;
  // takenOn against plannedOn; null when either is missing.
  timing: "on_day" | "late" | "early" | null;
  // Her answer to the dose question; null when she was not asked.
  doseReport: "as_prescribed" | "different" | null;
  // Patient-reported, only with doseReport = different. Never changes the
  // prescription.
  reportedDose: string | null;
  // The ordination in force on the dose's day, as written.
  prescribed: {
    medication: string | null;
    dose: string;
    frequency: string | null;
  };
  // TES-1151 S6 (Ö-415) — her optional reason for a deviating answer
  // (DOSE_REASONS), and her "Annat" text. Patient-reported.
  reason: "side_effects" | "forgot" | "no_medication" | "other" | null;
  reasonNote: string | null;
  // ISO instant she registered it (for a corrected entry: the correction).
  reportedAt: string;
  // Who gave this version: always the patient, except a first dose the
  // doctor corrected after her own 24 hours (TES-1455, Ö-687).
  reportedBy: DoseLogReporter;
  // TES-1151 S8 (Ö-417) — the answers this entry replaced, oldest first;
  // empty when she never corrected it. Corrections are versioned, never
  // edits.
  corrections: DoseLogCorrectionView[];
}

export type DoseLogReporter = "patient" | "doctor";

export interface DoseLogCorrectionView {
  outcome: "taken" | "not_taken";
  takenOn: string | null;
  reason: DoseLogEntryView["reason"];
  // ISO instant this answer was given.
  reportedAt: string;
  // Who gave it (TES-1455).
  reportedBy: DoseLogReporter;
}

// TES-1455 (Ö-687) — POST employee-api/v1/patients/:patientId/dose-log/
// first-dose/correction is the doctor's correction of her first dose's date
// after her own 24 hours. The body is the patient's (`reportFirstDoseSchema`,
// `{ doseDate: "YYYY-MM-DD" }`); the answer is the dose log again.

export interface DoseLogPauseView {
  // ISO instants; `from` is null only for an end with no recorded start,
  // `to` is null while the pause runs.
  from: string | null;
  to: string | null;
  // resumed | ended; null while paused.
  endedBy: "resumed" | "ended" | null;
}

// GET employee-api/v1/patients/:patientId/dose-log.
export interface DoseLogView {
  // Newest first, by the dose's day (takenOn, else plannedOn).
  entries: DoseLogEntryView[];
  // Newest first.
  pauses: DoseLogPauseView[];
  // TES-1491 (Ö-672) — which first dose her `first_dose` entry is: the start
  // of a new treatment ("Första dosen i behandlingen") or, for a prescription
  // transfer, her first dose under Ella's care ("Första dosen hos Ella").
  // Her patient group, as resolvePatientGroup reads it.
  firstDoseKind: "new_treatment" | "transfer";
}
